import dayjs, { Dayjs } from 'dayjs';
import parseMs from 'parse-ms';
import { DateValueModel, Expiration, OptionExpirationType } from '@tradingblock/types';
import _ from 'lodash';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const isSameDate = (date1: Date | dayjs.Dayjs | undefined, date2: Date | dayjs.Dayjs | undefined) => {
  if (date1 && date2) {
    return dayjs(date1).isSame(date2, 'date');
  }
  return false;
};

export function isToday(date: Date | undefined): boolean {
  return date ? isSameDate(date, new Date()) : false;
}

export const MarketOpenTime = () => {
  const isDST = dayjs.tz(undefined, 'America/New_York').utcOffset() === -240 ? true : false;

  if (isDST) {
    return dayjs
      .utc()
      .hour(13)
      .minute(30)
      .second(0)
      .millisecond(0);
  }

  return dayjs
    .utc()
    .hour(14)
    .minute(30)
    .second(0)
    .millisecond(0);
};

export const TimeTillClose = () => {
  const isDST = dayjs.tz(undefined, 'America/New_York').utcOffset() === -240 ? true : false;

  if (isDST) {
    return dayjs
      .utc()
      .set('hour', 20)
      .set('minute', 0)
      .set('second', 0);
  }

  return dayjs
    .utc()
    .set('hour', 21)
    .set('minute', 0)
    .set('second', 0);
};

export const PreviewTimeTillClose = () => {
  const isDST = dayjs.tz(undefined, 'America/New_York').utcOffset() === -240 ? true : false;

  if (isDST) {
    return dayjs
      .utc()
      .set('hour', 20)
      .set('minute', 15)
      .set('second', 0);
  }

  return dayjs
    .utc()
    .set('hour', 21)
    .set('minute', 15)
    .set('second', 0);
};

export const isMarketOpen = (dayInterval?: boolean) => {
  const now = dayjs.utc().toDate();

  // if day interval return true
  // otherwise afterhours the current day's bar will not build
  // TODO: need to determine if a bar has been built for after hours 1D interval, if so return false otherwise we will return all quotes received after hours
  if (dayInterval) {
    return true;
  }

  const marketOpen = MarketOpenTime().toDate();
  const timeTillClose = TimeTillClose().toDate();

  return isBetween(now, marketOpen, timeTillClose);
};
export const PreviewisMarketOpen = () => {
  const now = dayjs.utc().toDate();
  const marketOpen = MarketOpenTime().toDate();
  const timeTillClose = PreviewTimeTillClose().toDate();

  return isBetween(now, marketOpen, timeTillClose);
};
export const isTodayOrAfter = (date: Date | dayjs.Dayjs) =>
  // assuming that if today, should be before market closes
  (isSameDate(date, dayjs()) && dayjs().isBefore(TimeTillClose())) || dayjs().isBefore(date);

export function isExpiration<T>(value: Expiration | T): value is Expiration {
  return _.keys(value).includes('date');
}
export const expirationToDayjs = (date: Date | Expiration) => {
  if (isExpiration(date)) {
    return dayjs(date.date);
  }
  return dayjs(date);
};
export const expirationUnixTimestamp = (date: Date | Expiration | undefined) =>
  date === undefined ? -1 : expirationToDayjs(date).valueOf();
export const getSimpleDateString = (date: Date | Expiration) => {
  return expirationToDayjs(date).format('YYMMDD:HHmmss');
};
export const expirationValue = (date: Expiration) => {
  return `${getSimpleDateString(date)} ${OptionExpirationType[date.optionExpirationType]}`;
};
export const isSameExpiration = (date1: Expiration, date2: Expiration) =>
  expirationValue(date1) === expirationValue(date2);

export const DisplayTimeTilClose = (parsed: parseMs.Parsed) => {
  return `${parsed.hours} hour${parsed.hours === 1 ? '' : 's'}, ${parsed.minutes} minute${
    parsed.minutes === 1 ? '' : 's'
  }`;
};

/**
 * Checks to see whether or not the given date is between two dates
 *
 * @param date the Date to be checked
 * @param startDate start of date range
 * @param endDate end of date range
 */
export const isBetween = (date: Date | undefined, startDate: Date, endDate: Date) => {
  if (date) {
    const d = dayjs(date);
    return d.isAfter(startDate) && d.isBefore(endDate);
  }
  return false;
};

// is startingDate in year of 1970
export const isStartingDateIn1970 = (startingDate: Dayjs) => {
  return startingDate.year() === 1970;
};

export function daterangeToTimeframe(
  startingDate: dayjs.Dayjs | null | undefined,
  endingDate: dayjs.Dayjs | null | undefined
): string {
  if (!startingDate || !endingDate || isStartingDateIn1970(startingDate)) {
    return 'All';
  }
  const today = dayjs();
  if (isSameDate(startingDate, today) && isSameDate(endingDate, today)) {
    return 'Today';
  }
  if (isSameDate(startingDate, today.subtract(1, 'week')) && isSameDate(endingDate, today)) {
    return 'Week';
  }
  if (isSameDate(startingDate, today.subtract(1, 'month')) && isSameDate(endingDate, today)) {
    return 'Month';
  }
  if (isSameDate(startingDate, today.subtract(1, 'year')) && isSameDate(endingDate, today)) {
    return 'Year';
  }
  const format = 'M/D/YY';
  return `${startingDate.format(format)} - ${endingDate.format(format)}`;
}

export const formatDate = (date: dayjs.Dayjs | string) => {
  return (_.isString(date) ? dayjs(date) : date).format('MMM D, YYYY h:mm A');
};

export const formatDateWithoutTime = (date: dayjs.Dayjs | string) => {
  return (_.isString(date) ? dayjs(date) : date).format('MMM D, YYYY');
};

export const cleanDatePart = (value: number | undefined) => {
  return value && `${value < 10 ? '0' : ''}${value}`;
};

export const cleanDateModel = (value: DateValueModel | null | undefined) => {
  if (value) {
    return `${cleanDatePart(value.year)}-${cleanDatePart(value.month)}-${cleanDatePart(value.day)}`;
  }
  return null;
};
